import { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { Trophy, Search, Loader2, ArrowUpRight, GraduationCap } from 'lucide-react';

export default function AdminGradebook() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchGrades = async () => {
      setLoading(true);
      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        const usersMap = {};
        usersSnap.forEach(d => { usersMap[d.id] = { id: d.id, ...d.data() }; });

        const coursesSnap = await getDocs(collection(db, 'courses'));
        const coursesMap = {};
        coursesSnap.forEach(d => {
          const c = d.data();
          const totalLessons = c.curriculum?.reduce((sum, u) => sum + (u.lessons?.length || 0), 0) || 1;
          coursesMap[d.id] = { title: c.title, totalLessons };
        });

        const enrSnap = await getDocs(collection(db, 'enrollments'));
        const records = [];
        enrSnap.forEach(d => {
          const e = d.data();
          const courseInfo = coursesMap[e.courseId];
          if (!courseInfo) return; // if course deleted

          const totalLessons = courseInfo.totalLessons;
          const points = e.pointsEarned || 0;
          const totalPossible = totalLessons * 100;
          const percent = totalPossible > 0 ? Math.round((points / totalPossible) * 100) : 0;
          const isFinished = (e.completedLessons?.length || 0) >= totalLessons;

          let statusLabel = 'قيد الدراسة';
          let statusColor = 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400';

          if (isFinished) {
            statusLabel = percent >= 75 ? 'مجتاز' : 'راسب';
            statusColor = percent >= 75
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400'
              : 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400';
          }

          const uInfo = usersMap[e.userId] || {};

          records.push({
            id: d.id,
            userName: uInfo.name || uInfo.nameAr || 'طالب غير معروف',
            userEmail: uInfo.email || '',
            courseTitle: courseInfo.title,
            points,
            totalPossible,
            percent,
            statusLabel,
            statusColor,
            isFinished
          });
        });

        setData(records.sort((a, b) => b.points - a.points));
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchGrades();
  }, []);

  const filtered = data.filter(r =>
    r.userName.toLowerCase().includes(search.toLowerCase()) ||
    r.courseTitle.toLowerCase().includes(search.toLowerCase()) ||
    r.userEmail.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto" dir="rtl">
      {/* Header */}
      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/5 rounded-2xl p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3">
            <Trophy className="w-7 h-7 text-primary-500" /> سجّل الدرجات (Gradebook)
          </h2>
          <p className="text-slate-500 dark:text-zinc-400 font-medium text-md mt-1">
            متابعة تقدم الطلاب وعلاماتهم وإحصائيات النجاح في جميع الدورات المسجلة.
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/5 shadow-sm rounded-2xl overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-200 dark:border-white/5 bg-slate-50/50 dark:bg-black/20 flex items-center justify-between gap-4">
          <div className="relative max-w-sm w-full">
            <input
              type="text"
              placeholder="ابحث عن اسم طالب أو دورة..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full bg-white dark:bg-zinc-950 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white rounded-xl pl-10 pr-4 py-2.5 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all font-medium text-md"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          </div>
        </div>

        {loading ? (
          <div className="p-20 flex justify-center"><Loader2 className="w-10 h-10 animate-spin text-primary-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-20 text-center text-slate-400 font-bold">لا يوجد نتائج للبحث.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-right" dir="rtl">
              <thead className="bg-slate-50 dark:bg-zinc-900/50 text-slate-500 dark:text-zinc-400 text-md font-bold border-b border-slate-200 dark:border-white/5">
                <tr>
                  <th className="p-4">الطالب</th>
                  <th className="p-4">الدورة</th>
                  <th className="p-4">إجمالي المنح (نقاط)</th>
                  <th className="p-4">النسبة (%)</th>
                  <th className="p-4">حالة الإنجاز</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {filtered.map(row => (
                  <tr key={row.id} className="hover:bg-slate-50/50 dark:hover:bg-white/5 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-sm shadow-primary-500/20">
                          {row.userName.charAt(0)}
                        </div>
                        <div>
                          <p className="font-bold text-slate-900 dark:text-white leading-tight">{row.userName}</p>
                          <p className="text-md text-slate-500 dark:text-zinc-500 mt-0.5" dir="ltr">{row.userEmail}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="font-bold text-slate-700 dark:text-zinc-300 text-md flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-slate-400" /> {row.courseTitle}
                      </span>
                    </td>
                    <td className="p-4 font-mono font-bold">
                      <span className="text-primary-600 dark:text-primary-400">{row.points}</span>
                      <span className="text-slate-400 dark:text-zinc-600 text-md"> / {row.totalPossible}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 max-w-[100px] h-2 bg-slate-100 dark:bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full bg-primary-500 rounded-full" style={{ width: `${row.percent}%` }}></div>
                        </div>
                        <span className="font-bold text-md text-slate-700 dark:text-zinc-300 w-10">{row.percent}%</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1 rounded-full text-md font-bold ring-1 ring-inset ring-current/20 ${row.statusColor}`}>
                        {row.statusLabel}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
